import { useQuery } from "@tanstack/react-query";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import { get } from "api/api-methods";
import { useParams } from "react-router-dom";
import { ArticleStock } from "types/article-stock";

export function useArticleStocks() {
    const { data: reservation } = useWebBooking();
    const { system } = useParams();

    return useQuery({
        queryKey: ["articleStocks", reservation?.guid, system],
        queryFn: async () => {
            if (!reservation?.menus || !system) {
                return new Map<number, ArticleStock[]>();
            }

            // Collect all article IDs from the menus
            const articleIds: number[] = [];
            reservation.menus.forEach((menu) => {
                menu.groups?.forEach((group) => {
                    group.items?.forEach((item) => {
                        if (item.articleID && !articleIds.includes(item.articleID)) {
                            articleIds.push(item.articleID);
                        }
                    });
                });
            });

            if (articleIds.length === 0) {
                return new Map<number, ArticleStock[]>();
            }

            // Use the booking start time as validity date
            const validityDate = reservation.start;
            const formattedDate = validityDate.format("YYYY-MM-DDTHH:mm:ss") + ".000Z";

            try {
                const response = await get<ArticleStock[]>(
                    `WebBooking/WebBookings/articleStocks?articleIds=${articleIds.join(",")}&includeArticleStockChanges=false&validityDate=${formattedDate}`
                );

                // Convert response to ArticleStock instances and group by articleId
                const articleStocks = response.map(
                    (data: Partial<ArticleStock>) => new ArticleStock(data)
                );
                const groupedStocks = new Map<number, ArticleStock[]>();

                articleStocks.forEach((stock) => {
                    if (!groupedStocks.has(stock.articleId)) {
                        groupedStocks.set(stock.articleId, []);
                    }
                    groupedStocks.get(stock.articleId)?.push(stock);
                });

                return groupedStocks;
            } catch (error) {
                console.error("Failed to load article stocks:", error);
                return new Map<number, ArticleStock[]>();
            }
        },
        enabled:
            !!reservation?.guid && !!system && !!reservation?.menus && reservation.menus.length > 0,
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchOnWindowFocus: false,
    });
}
